import * as React from "react";
import { ViewState } from "@devexpress/dx-react-scheduler";
import {
  Scheduler,
  DayView,
  Appointments,
  ViewSwitcher,
  Toolbar,
  DateNavigator,
  TodayButton,
  MonthView,
  WeekView,
  AppointmentForm,
  AppointmentTooltip,
} from "@devexpress/dx-react-scheduler-material-ui";
import dayjs from "dayjs";
import TooltipAgenda from "./TooltipAgenda";
import { useEffect, useState } from "react";
import FormularioCadastrarAgendameto from "./FormularioAgenda";
import { Agendamento } from "../../model/entities/Agendamento";
import agendamentoApi from "../../api/agendamentoApi";
import DialogConfirmGeneric from "../../components/Generics/DialogConfirmGeneric";
import Container from "../../components/Generics/Container";
import GridCenter from "../../components/Generics/GridCenter";
import { useSnack } from "../../redux/snack/snackHooks";
import { useSessao } from "../../redux/sessao/sessaoHooks";
import { Tooltip, Typography } from "@mui/material";

const CustonViewSwitcher: React.FC<ViewSwitcher.SwitcherProps> = ({
  children,
  ...restProps
}: any) => (
  <ViewSwitcher.Switcher {...restProps}>{children}</ViewSwitcher.Switcher>
);

const CustonDateNavigator: React.FC<DateNavigator.NavigationButtonProps> = ({
  ...restProps
}) => <DateNavigator.NavigationButton {...restProps} />;

const Button: React.FC<TodayButton.ButtonProps> = ({ ...restProps }) => (
  <TodayButton.Button
    {...restProps}
    getMessage={() => "Hoje"}
    style={{
      backgroundColor: "#fff",
      borderRadius: "8px",
    }}
  />
);

const CustonMonthView: React.FC<MonthView.LayoutProps> = ({ ...restProps }) => (
  <MonthView.Layout {...restProps} style={{ height: "800px" }} />
);

type CustomTimeTableCellProps = MonthView.TimeTableCellProps & {
  onSelectDate: (date: Date) => void;
  agendamentos: Agendamento[];
};

const CustomTimeTableCell: React.FC<CustomTimeTableCellProps> = ({
  startDate,
  onSelectDate,
  agendamentos,
  ...restProps
}) => {
  const day = startDate.getDay();
  const isWeekend = day === 0 || day === 6;
  const formattedDate = dayjs(startDate).format("YYYY-MM-DD");
  const isFeriado = agendamentos.some(
    (item) =>
      item.diaOff === true &&
      dayjs(item.startDate).format("YYYY-MM-DD") === formattedDate
  );

  const isDisabled = isWeekend || isFeriado;

  return (
    <MonthView.TimeTableCell
      {...restProps}
      startDate={startDate}
      style={{
        backgroundColor: isDisabled ? "#c4c3c3" : undefined,
        pointerEvents: isDisabled ? "none" : undefined, // Desabilita clique
        opacity: isDisabled ? 0.6 : 1, // Visualmente "desabilitado"
      }}
      onClick={() => onSelectDate(startDate)}
    />
  );
};

//React.FC<Appointments.AppointmentProps>
const Appointment: React.FC<Appointments.AppointmentProps> = ({
  data,
  ...restProps
}) => {
  const isFeriado = data.diaOff === true;
  return (
    <Appointments.Appointment
      {...restProps}
      data={data}
      style={{
        backgroundColor: isFeriado ? "#ffcdd2" : "#90caf9",
        borderRadius: "8px",
        border: isFeriado ? "2px solid #f44336" : "none",
        color: "#000",
        padding: "4px",
      }}
    >
      <Tooltip title={isFeriado ? "Feriado" : data.title}>
        <div>
          <Typography
            variant="body2"
            fontWeight={isFeriado ? "bold" : "normal"}
            color={isFeriado ? "#b71c1c" : "inherit"}
          >
            {isFeriado ? "Feriado: " + data.title : data.title}
          </Typography>
        </div>
      </Tooltip>
    </Appointments.Appointment>
  );
};

const CustonAppointmentTooltip: React.FC<AppointmentTooltip.ContentProps> = (
  props
) => {
  const isFeriado = props.appointmentData?.diaOff === true;
  if (isFeriado) return null;
  return <TooltipAgenda id={props.appointmentData?.id} />;
};

const CustomCommandButton = (props: AppointmentForm.CommandLayoutProps) => {
  return null;
};
type Props = {
  containerPage?: boolean;
  naoMostrarErro?: boolean;
};

const Agenda = (props: Props) => {
  const { containerPage, naoMostrarErro } = props;
  const mostrarSnack = useSnack();

  const [openALertExcluirAgendamento, setOpenALertExcluirAgendamento] =
    useState(false);
  const [idAgendamentoDeletar, setIdAgendamentoDeletar] = useState(0);
  const [isTooltipVisible, setIsTooltipVisible] = useState(true);
  const [sessao] = useSessao();
  const perfil = sessao.usuario?.perfil;
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [openForm, setOpenForm] = useState(false);
  const [agendamentos, setAgendamentos] = useState<Agendamento[]>([]);
  const [currentView, setCurrentView] = useState("Week");

  const excluirAgendamento = async () => {
    try {
      await agendamentoApi.delete(idAgendamentoDeletar);
      mostrarSnack("Agendamento excluido!", "info");
      setOpenALertExcluirAgendamento(false);
      setIsTooltipVisible(true);
      getAgendamentos();
    } catch (erro) {
      mostrarSnack("Não foi possível deletar o agendamento", "error");
    }
  };

  useEffect(() => {
    getAgendamentos();
  }, []);

  const getAgendamentos = async () => {
    try {
      const res = await agendamentoApi.getManyComplete();

      setAgendamentos(res);
    } catch (erro) {
      if (!naoMostrarErro)
        mostrarSnack("Não consegui buscar os agendamentos", "error");
      console.log('"Não consegui buscar os agendamentos', erro);
    }
  };

  const CustomAppointmentTooltipLayout: React.FC<
    AppointmentTooltip.LayoutProps
  > = (props) => {
    const isFeriado = props.appointmentMeta?.data?.diaOff === true;

    if (isFeriado) return null;
    return (
      <AppointmentTooltip.Layout
        {...props}
        contentComponent={CustonAppointmentTooltip}
        onDeleteButtonClick={() => {
          const id = Number(props.appointmentMeta?.data.id);
          setIdAgendamentoDeletar(id ?? 0);
          setOpenALertExcluirAgendamento(true);
          setIsTooltipVisible(false);
        }}
      />
    );
  };

  const FormularioCriarAgenadamento: React.FC<
    AppointmentForm.BasicLayoutProps
  > = (props) => {
    return (
      <FormularioCadastrarAgendameto
        dataAgendamento={dayjs(props.appointmentData.startDate).format(
          "DD/MM/YYYY"
        )}
        onClose={() => setOpenForm(false)}
        idAgendamento={Number(props.appointmentData.id) ?? 0}
        onReload={getAgendamentos}
      />
    );
  };

  return (
    <GridCenter>
      <Container page={containerPage}>
        <Scheduler data={agendamentos} locale={"pt-BR"}>
          <ViewState
            currentDate={currentDate.toDate()}
            onCurrentDateChange={(date) => setCurrentDate(dayjs(date))}
            currentViewName={currentView}
            onCurrentViewNameChange={setCurrentView}
          />
          <DayView
            startDayHour={7}
            endDayHour={19}
            displayName="Dia"
            cellDuration={60}
          />

          <MonthView
            displayName="Mês"
            layoutComponent={CustonMonthView}
            timeTableCellComponent={(props) => (
              <CustomTimeTableCell
                {...props}
                agendamentos={agendamentos}
                onSelectDate={(date) => {
                  setCurrentDate(dayjs(date));
                  setCurrentView("Day");
                }}
              />
            )}
          />
          <WeekView
            startDayHour={7}
            endDayHour={19}
            displayName="Semana"
            cellDuration={60}
            excludedDays={[0, 6]}
          />

          <Appointments appointmentComponent={Appointment} />

          {isTooltipVisible && (
            <AppointmentTooltip
              showCloseButton
              showOpenButton={perfil?.podeEditarAgendamento}
              showDeleteButton={perfil?.podeEditarAgendamento}
              layoutComponent={CustomAppointmentTooltipLayout}
            />
          )}

          <AppointmentForm
            basicLayoutComponent={FormularioCriarAgenadamento}
            commandLayoutComponent={CustomCommandButton}
            onVisibilityChange={(valor) => {
              if (sessao.usuario?.perfil.podeEditarAgendamento) {
                setOpenForm(valor);
              }
            }}
            visible={openForm}
          />

          <Toolbar />
          <DateNavigator navigationButtonComponent={CustonDateNavigator} />
          <ViewSwitcher switcherComponent={CustonViewSwitcher} />
          <TodayButton buttonComponent={Button} />
        </Scheduler>

        <DialogConfirmGeneric
          open={openALertExcluirAgendamento}
          cabecalho={"Excluir ?"}
          texto={
            "Deseja realmente excluir o agendamento? Essa ação não poderá ser desfeita."
          }
          onResposta={(res) => {
            if (res) {
              excluirAgendamento();
            } else {
              setOpenALertExcluirAgendamento(false);
              setIsTooltipVisible(true);
            }
          }}
        />
      </Container>
    </GridCenter>
  );
};

export default Agenda;
